import {
  hasPermissions,
  getUserPermissions,
  getCurrentAccount,
} from './permissionsHelper';

import {
  ROLES,
  CONVERSATION_PERMISSIONS,
  CONTACT_PERMISSIONS,
  REPORTS_PERMISSIONS,
  PORTAL_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

export const defaultRedirectPage = (to, permissions) => {
  const { accountId } = to.params;

  const permissionRoutes = [
    {
      permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
      path: 'dashboard',
    },
    { permissions: [CONTACT_PERMISSIONS], path: 'contacts' },
    { permissions: [REPORTS_PERMISSIONS], path: 'reports/overview' },
    { permissions: [PORTAL_PERMISSIONS], path: 'portals' },
  ];

  const route = permissionRoutes.find(({ permissions: routePermissions }) =>
    hasPermissions(routePermissions, permissions)
  );

  return `accounts/${accountId}/${route ? route.path : 'dashboard'}`;
};

const validateActiveAccountRoutes = (to, user, store) => {
  // If the current account is active, then check for the route permissions
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  // 白名单路由：允许订阅过期的用户访问这些路由（用于续订等操作），跳过权限检查
  const allowedRoutesForExpiredSubscription = [
    'billing_settings_index', // 账单设置
    'subscription_plan', // 订阅套餐/续订
    'subscription_usage', // 订阅使用详情
    'subscription_invoices', // 订阅发票
    'settings_profile', // 个人资料
  ];

  // 检查订阅是否过期（直接从 store 获取账户数据，确保数据完整性）
  const accountId = Number(to.params.accountId);
  let currentAccount,
    subscriptionEndsOn,
    isSubscriptionExpired = false;

  // 优先从 store 获取完整数据，如果 store 不可用则回退到 user 对象
  if (store && store.getters && store.getters['accounts/getAccount']) {
    currentAccount = store.getters['accounts/getAccount'](accountId);
    if (currentAccount) {
      const subscription =
        currentAccount.custom_attributes?.subscription ||
        currentAccount.custom_attributes;
      subscriptionEndsOn = subscription?.subscription_ends_on;
    }
  }

  // 回退到从 user 对象获取（兼容旧逻辑）
  // 检查 currentAccount 是否有效，或者 subscriptionEndsOn 是否为空
  if (!currentAccount || !currentAccount.id || !subscriptionEndsOn) {
    const accountFromUser = getCurrentAccount(user, accountId);
    if (accountFromUser) {
      currentAccount = accountFromUser;
      subscriptionEndsOn =
        currentAccount?.custom_attributes?.subscription?.subscription_ends_on ||
        currentAccount?.custom_attributes?.subscription_ends_on;
    }
  }

  if (subscriptionEndsOn) {
    const endDate = new Date(subscriptionEndsOn);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    isSubscriptionExpired = daysLeft < 0;
  }

  // 如果订阅已过期
  if (isSubscriptionExpired) {
    // 如果目标路由在白名单中，直接允许访问（跳过所有后续检查）
    if (allowedRoutesForExpiredSubscription.includes(to.name)) {
      return null;
    }

    // 如果目标路由不在白名单中，重定向到 dashboard
    return accountDashboardURL;
  }

  // 订阅未过期，正常权限检查
  const userPermissions = getUserPermissions(user, to.params.accountId);

  const isAccessible = routeIsAccessibleFor(to, userPermissions);
  // If the route is not accessible for the user, return to dashboard screen
  return isAccessible ? null : defaultRedirectPage(to, userPermissions);
};

export const validateLoggedInRoutes = (to, user, store) => {
  // 调试：确保代码被执行
  if (typeof window !== 'undefined') {
    console.log('[validateLoggedInRoutes] === ENTRY ===', to.name);
  }

  const accountId = Number(to.params.accountId);
  let currentAccount,
    subscriptionEndsOn,
    isSubscriptionExpired = false;

  console.log('[validateLoggedInRoutes] === ENTRY ===', {
    routeName: to.name,
    accountId,
    hasStore: !!store,
    hasStoreGetters: !!(store && store.getters),
    hasUser: !!user,
  });

  // 优先从 store 获取完整数据
  if (store && store.getters && store.getters['accounts/getAccount']) {
    console.log('[validateLoggedInRoutes] Trying to get account from store...');
    currentAccount = store.getters['accounts/getAccount'](accountId);
    console.log('[validateLoggedInRoutes] Account from store:', currentAccount);
    if (currentAccount) {
      const subscription =
        currentAccount.custom_attributes?.subscription ||
        currentAccount.custom_attributes;
      subscriptionEndsOn = subscription?.subscription_ends_on;
      console.log('[validateLoggedInRoutes] Subscription data:', {
        subscription,
        subscriptionEndsOn,
      });
    }
  }

  // 回退到从 user 对象获取（兼容旧逻辑）
  // 检查 currentAccount 是否存在且有 id 属性（空对象 {} 会被 !currentAccount 判断为 truthy）
  if (!currentAccount || !currentAccount.id) {
    console.log(
      '[validateLoggedInRoutes] No valid account from store, trying user object...'
    );
    console.log('[validateLoggedInRoutes] User accounts:', user?.accounts);
    currentAccount = getCurrentAccount(user, accountId);
    console.log(
      '[validateLoggedInRoutes] Account from user object:',
      currentAccount
    );
  }
  if (!subscriptionEndsOn && currentAccount) {
    subscriptionEndsOn =
      currentAccount?.custom_attributes?.subscription?.subscription_ends_on ||
      currentAccount?.custom_attributes?.subscription_ends_on;
    console.log(
      '[validateLoggedInRoutes] SubscriptionEndsOn after fallback:',
      subscriptionEndsOn
    );
  }

  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    console.log(
      '[validateLoggedInRoutes] No account found, returning to login'
    );
    return `app/login`;
  }

  // 白名单路由：允许订阅过期但账户活跃的用户访问这些路由（用于续订等操作）
  const allowedRoutesForExpiredSubscription = [
    'billing_settings_index', // 账单设置
    'subscription_plan', // 订阅套餐/续订
    'subscription_usage', // 订阅使用详情
    'subscription_invoices', // 订阅发票
    'settings_profile', // 个人资料
  ];

  // 检查订阅是否过期
  if (subscriptionEndsOn) {
    const endDate = new Date(subscriptionEndsOn);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    isSubscriptionExpired = daysLeft < 0;
  }

  // 如果订阅已过期
  if (isSubscriptionExpired) {
    console.log(
      '[validateLoggedInRoutes] Subscription expired, route:',
      to.name,
      'whitelist:',
      allowedRoutesForExpiredSubscription
    );

    // 如果目标路由在白名单中，直接允许访问（跳过所有后续检查）
    if (allowedRoutesForExpiredSubscription.includes(to.name)) {
      return null;
    }

    // 如果不在白名单中，且不是在 dashboard 页面，重定向到 dashboard
    if (to.name !== 'home' && to.name !== 'dashboard_home') {
      console.log(
        '[validateLoggedInRoutes] Not in whitelist, redirecting to dashboard'
      );
      return `accounts/${to.params.accountId}/dashboard`;
    }

    // 在 dashboard 页面，允许访问（显示过期遮罩层）
    console.log('[validateLoggedInRoutes] On dashboard, showing overlay');
    return null;
  }

  // 账户状态：0=active, 1=suspended
  // 需要同时检查字符串和整数形式（API 可能返回字符串，数据库返回整数）
  const isCurrentAccountActive =
    currentAccount.status === 'active' || currentAccount.status === 0;

  console.log('[validateLoggedInRoutes] Account status check:', {
    status: currentAccount.status,
    statusType: typeof currentAccount.status,
    isCurrentAccountActive,
  });

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user, store);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    console.log(
      '[validateLoggedInRoutes] Account not active, redirecting to suspended'
    );
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = routeName => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = routeName =>
  routeName === 'notifications_index';
// TEST: Sun Apr  5 20:34:12 CST 2026
